import { motion } from "framer-motion";
import YearSelect from "./YearSelect";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut, Pie } from "react-chartjs-2";
import { buildSlices, formatCount, formatMoney } from "./dashboardData";

ChartJS.register(ArcElement, Tooltip, Legend);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleColor: "white",
      bodyColor: "white",
      borderColor: "rgba(255, 255, 255, 0.1)",
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
    },
  },
};

export default function ProductPerformanceOverview({ year, setYear, data, loading }) {
  const currency = data?.currency || "GHS";
  const products = Array.isArray(data?.products) ? data.products : [];
  const revenueSlices = buildSlices(products, "product_name", "revenue");
  const salesSlices = buildSlices(products, "product_name", "sales_count");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.9 }}
      className="mt-8"
    >
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Product Performance Overview</h2>
        <YearSelect
          year={year}
          onChange={setYear}
          className="border-gray-300 focus:border-gray-400 focus:ring-gray-200"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-800">Revenue by product</h3>
          <div className="mb-6 flex items-center justify-center">
            <div className="relative h-48 w-48">
              <Doughnut
                data={{
                  labels: revenueSlices.labels,
                  datasets: [{ data: revenueSlices.values, backgroundColor: revenueSlices.colors, borderWidth: 0, cutout: "60%" }],
                }}
                options={chartOptions}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                <div className="text-sm font-bold text-gray-800">
                  {loading || revenueSlices.empty ? "---" : formatMoney(revenueSlices.total, currency)}
                </div>
                <div className="text-sm text-gray-600">Revenue</div>
              </div>
            </div>
          </div>
          {revenueSlices.empty ? (
            <p className="text-sm text-gray-500">{loading ? "Loading..." : `No data for ${year}`}</p>
          ) : (
            <div className="space-y-3">
              {revenueSlices.rows.map((row, index) => (
                <div key={`${row.label}-${index}`} className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center">
                    <div className="mr-3 h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: revenueSlices.colors[index] }} />
                    <span className="truncate text-sm text-gray-700">{row.label}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{formatMoney(row.value, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-800">Product</h3>
          <div className="mb-6 flex items-center justify-center">
            <div className="relative h-48 w-48">
              <Pie
                data={{
                  labels: salesSlices.labels,
                  datasets: [{ data: salesSlices.values, backgroundColor: salesSlices.colors, borderWidth: 0 }],
                }}
                options={chartOptions}
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="text-lg font-bold text-gray-800">
                  {loading || salesSlices.empty ? "---" : formatCount(salesSlices.total)}
                </div>
              </div>
            </div>
          </div>
          {salesSlices.empty ? (
            <p className="text-sm text-gray-500">{loading ? "Loading..." : `No data for ${year}`}</p>
          ) : (
            <div className="space-y-3">
              {salesSlices.rows.map((row, index) => (
                <div key={`${row.label}-${index}`} className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center">
                    <div className="mr-3 h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: salesSlices.colors[index] }} />
                    <span className="truncate text-sm text-gray-700">{row.label}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{formatCount(row.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
